import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { roleAllowed } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Table2, Filter, Download, Loader2, RotateCcw, Check, Pencil, PackageMinus, Trash2 } from "lucide-react";
import { docCurrency, totalsByCurrency, formatCurrencyTotals } from "@/lib/currency";

// Sheet layout — exact column order per spec. `kind` drives formatting +
// the filter UI; every column here is read-only, sourced live from
// Transfer Orders / Sales Orders / Delivery Challans / Production Flow /
// Invoices — see backend/order_status_sheet.py for exactly where each one
// comes from. The one exception is "delivery_date" (`editable: true`
// below): it's inline-editable right here, and saving it syncs the same
// value onto the linked Sales Order — see the DeliveryDateCell component.
const COLUMNS = [
  { field: "month",                   header: "Month",                              kind: "text" },
  { field: "date",                    header: "Date",                               kind: "date" },
  { field: "to_no",                   header: "TO No",                              kind: "text" },
  { field: "marketing",               header: "Marketing",                          kind: "text" },
  { field: "customer_name",           header: "Customer Name",                      kind: "text" },
  { field: "description",             header: "Description",                        kind: "text" },
  { field: "part_number",             header: "Part Number",                        kind: "text" },
  { field: "ord_qty",                 header: "Ord. Qty",                           kind: "number" },
  { field: "unit",                    header: "Unit",                               kind: "text" },
  { field: "currency",                header: "Currency",                           kind: "text" },
  { field: "price",                   header: "Price",                              kind: "currency" },
  { field: "discount_pct",            header: "% Discount",                         kind: "percent" },
  { field: "final_price",             header: "Final Price",                        kind: "currency" },
  { field: "delivery_date",           header: "Delivery Date",                      kind: "date", editable: true },
  { field: "final_delivery",          header: "Final Delivery",                     kind: "date" },
  { field: "sales_order_number",      header: "Sales Order Number",                 kind: "text" },
  { field: "packed_qty",              header: "Packed Qty",                         kind: "number" },
  { field: "packed_qty_value",        header: "Packed Qty Value",                   kind: "currency" },
  { field: "prd_comp_date",           header: "Prd. Comp. Date",                    kind: "date" },
  { field: "production_status",       header: "Production Status",                  kind: "badge" },
  { field: "balance",                 header: "Balance",                            kind: "number" },
  { field: "balance_material_value",  header: "Balance Material Value",             kind: "currency" },
  { field: "packed_status",           header: "Against Order Material Packed Status", kind: "badge" },
  { field: "dc_date",                 header: "DC Date",                            kind: "date" },
  { field: "dc_no",                   header: "DC No",                              kind: "text" },
  { field: "invoice_number",          header: "Invoice Number",                     kind: "text" },
  { field: "dispatch_date",           header: "Dispatch Date",                      kind: "date" },
  { field: "dispatch_status",         header: "Dispatch Status",                    kind: "badge" },
  { field: "payment_terms",           header: "Payment Terms",                      kind: "text" },
  { field: "delivery_through",        header: "Delivery Through",                   kind: "text" },
];

const MONTH_ORDER = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Fixed option order for the "Against Order Material Packed Status" filter —
// "Unpacked" always sits right under "Partially Packed", regardless of
// whether any row currently carries it.
const PACKED_STATUS_OPTIONS = ["Fully Packed", "Not Packed", "Partially Packed", "Unpacked"];

// Columns summed in the pinned totals row (and in the Excel export's totals
// line) — everything else in that row renders blank except the label cell.
const TOTAL_FIELDS = new Set(["ord_qty", "packed_qty", "packed_qty_value", "balance", "balance_material_value"]);

const BADGE_TONE = {
  "Fully Packed": "bg-emerald-50 text-emerald-800",
  "Partially Packed": "bg-amber-50 text-amber-800",
  "Not Packed": "bg-slate-100 text-slate-700",
  "Unpacked": "bg-sky-50 text-sky-800",
  "Completed": "bg-emerald-50 text-emerald-800",
  "In Production": "bg-amber-50 text-amber-800",
  "Work Order Raised": "bg-blue-50 text-blue-800",
  "Not Started": "bg-slate-100 text-slate-700",
  "dispatched": "bg-blue-50 text-blue-800",
  "delivered": "bg-emerald-50 text-emerald-800",
  "draft": "bg-slate-100 text-slate-700",
  "ready": "bg-sky-50 text-sky-800",
  "cancelled": "bg-rose-50 text-rose-800",
};

function fmtDate(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return v;
  }
}

function fmtNumber(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}

function fmtCurrency(v) {
  const n = Number(v || 0);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function displayValue(row, col) {
  if (col.field === "currency") return docCurrency(row);   // pre-currency rows are INR
  const v = row[col.field];
  switch (col.kind) {
    case "date": return fmtDate(v);
    case "number": return fmtNumber(v);
    case "currency": return fmtCurrency(v);
    case "percent": return v || v === 0 ? `${fmtNumber(v)}%` : "";
    default: return v == null ? "" : String(v);
  }
}

// One column's Excel/Google-Sheets-style filter — a searchable checkbox
// list of every distinct value that column takes across the whole sheet.
// Matches spreadsheet filter-by-values behavior exactly: checking any
// number of values is staged locally in this popover only; nothing on the
// table changes until "OK" is clicked (or discarded via "Cancel"/closing
// without OK). Typing in the search box only narrows which checkboxes are
// shown. "Select all" and "Clear" are both unconditional, absolute
// actions — Select all replaces the staged selection with exactly the
// currently-visible (searched) values, Clear empties it entirely —
// rather than merging with whatever was already staged. That keeps the
// result always predictable: searching "B13" and clicking Select all
// always isolates to just the B13 values, with no need to also remember
// to click Clear first (skipping that used to leave every other,
// now-hidden value still selected from the popover's default "everything
// checked" starting state, so the applied filter silently came out as
// "no filter" — show everything — even though only a handful of boxes
// looked checked). Individual checkbox clicks remain purely
// additive/toggling regardless of search, so building a selection across
// several different searches still works. Once applied, all rows
// carrying ANY of the checked values are kept (OR within the column);
// filters on different columns combine (AND) automatically since each is
// applied independently over the same row set.
function ColumnFilter({ col, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(() => new Set(selected ?? options));
  const active = selected != null && selected.size < options.length;

  // Re-stage from whatever's currently applied every time the popover
  // opens, so a Cancelled (or click-away-dismissed) edit from last time
  // never lingers into the next open.
  const handleOpenChange = (next) => {
    if (next) {
      setPending(new Set(selected ?? options));
      setSearch("");
    }
    setOpen(next);
  };

  const visibleOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, search]);

  const toggleValue = (val) => {
    setPending((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  };

  const selectAllVisible = () => {
    setPending(new Set(visibleOptions));
  };
  const clearAll = () => {
    setPending(new Set());
  };

  const applyFilter = () => {
    onChange(pending.size === options.length ? null : new Set(pending));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center justify-center rounded p-0.5 hover:bg-slate-200 ${active ? "text-blue-600" : "text-slate-400"}`}
          data-testid={`oss-filter-btn-${col.field}`}
          title={`Filter ${col.header}`}
        >
          <Filter className="w-3 h-3" fill={active ? "currentColor" : "none"} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start" data-testid={`oss-filter-popover-${col.field}`}>
        <div className="text-xs font-semibold text-slate-600 mb-1.5">{col.header}</div>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search values…"
          className="h-8 text-xs mb-2"
          data-testid={`oss-filter-search-${col.field}`}
        />
        <div className="flex items-center justify-between mb-1.5 text-[11px]">
          <button type="button" className="text-blue-600 hover:underline" onClick={selectAllVisible}>Select all</button>
          <button type="button" className="text-slate-500 hover:underline" onClick={clearAll}>Clear</button>
        </div>
        <div className="max-h-52 overflow-y-auto scroll-thin space-y-1 border-t border-slate-100 pt-1.5">
          {visibleOptions.length === 0 && (
            <div className="text-[11px] text-slate-400 py-2 text-center">No matching values</div>
          )}
          {visibleOptions.map((opt) => (
            <label key={opt || "(blank)"} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
              <Checkbox
                checked={pending.has(opt)}
                onCheckedChange={() => toggleValue(opt)}
                data-testid={`oss-filter-opt-${col.field}-${opt || "blank"}`}
              />
              <span className="truncate">{opt || <span className="text-slate-400 italic">(blank)</span>}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 mt-2 pt-2 border-t border-slate-100">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={() => setOpen(false)}
            data-testid={`oss-filter-cancel-${col.field}`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 px-2.5 text-xs"
            onClick={applyFilter}
            data-testid={`oss-filter-ok-${col.field}`}
          >
            OK
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// The sheet's one editable cell: click the Delivery Date to swap it for a
// native date picker; saving PATCHes the backend, which writes the date
// through to the Transfer Order line AND — when a Sales Order has already
// been raised off that line — mirrors it onto that Sales Order's own
// Delivery Date too, so both places agree without a page refresh. `onSaved`
// hands the parent the fresh, fully-recomputed row so every derived column
// (e.g. Final Delivery) updates in the same stroke.
function DeliveryDateCell({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(row.delivery_date || "");
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setValue(row.delivery_date || ""); }, [row.delivery_date]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const cancelEdit = () => { setValue(row.delivery_date || ""); setEditing(false); };

  const save = async () => {
    if (!editing) return;
    if (!value) { toast.error("Pick a date before saving"); return; }
    if (value === (row.delivery_date || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/order-status-sheet/rows/${row.id}`, { delivery_date: value });
      onSaved(data.row);
      setEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1800);
      toast.success(
        data.synced_to_sales_order
          ? `Delivery date saved — Sales Order ${data.row.sales_order_number || ""} updated to match`
          : "Delivery date saved"
      );
      if (data.sync_note) toast.warning(data.sync_note);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          ref={inputRef}
          type="date"
          value={value}
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
            if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
          }}
          className="h-7 text-xs w-[8.5rem]"
          data-testid={`oss-delivery-date-input-${row.id}`}
        />
        {saving && <Loader2 className="w-3 h-3 animate-spin text-slate-400 shrink-0" />}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Click to edit — saving updates the linked Sales Order too"
      className="group inline-flex items-center gap-1 rounded px-1 -mx-1 py-0.5 hover:bg-blue-50 hover:text-blue-700"
      data-testid={`oss-delivery-date-cell-${row.id}`}
    >
      <span>{fmtDate(row.delivery_date) || <span className="text-slate-300">—</span>}</span>
      {justSaved ? (
        <Check className="w-3 h-3 text-emerald-600" data-testid={`oss-delivery-date-saved-${row.id}`} />
      ) : (
        <Pencil className="w-2.5 h-2.5 text-slate-300 opacity-0 group-hover:opacity-100" />
      )}
    </button>
  );
}

// The "Against Order Material Packed Status" cell. For a row whose Packing
// Slip is still "packed" (i.e. Partially or Fully Packed, and not yet turned
// into a Delivery Challan), the badge itself is a dropdown: click it and an
// "Unpacked" option appears right under "Partially Packed" / "Fully Packed"
// — selecting it posts straight to that Packing Slip's own existing
// `/packing-slips/{id}/unpack` (the same route the Order Status Tracker and
// Packing Slips pages already use), which zeros its packed quantity. Packing
// only ever *reserves* against live FG_STORE stock (nothing is debited until
// a Delivery Challan is dispatched — see backend/server.py), so selecting
// Unpacked immediately drops that reservation and the quantity is available
// again as Finished Goods stock — reflected here as Packed Qty -> 0 and
// Balance -> back to the full Ord. Qty once the row list is refreshed.
// A row with nothing packed (or already dispatched) shows a plain,
// non-interactive badge — there's nothing to unpack.
function PackedStatusCell({ row, canUnpack, onUnpacked }) {
  const [open, setOpen] = useState(false);
  const [unpacking, setUnpacking] = useState(false);
  const canOfferUnpack = canUnpack && row.packing_slip_status === "packed" && Number(row.packed_qty) > 0;

  const badge = row.packed_status ? (
    <Badge className={`${BADGE_TONE[row.packed_status] || "bg-slate-100 text-slate-700"} font-normal border-0`}>
      {row.packed_status}
    </Badge>
  ) : (
    <span className="text-slate-300">—</span>
  );

  if (!canOfferUnpack) return badge;

  const unpack = async () => {
    setUnpacking(true);
    try {
      await api.post(`/packing-slips/${row.packing_slip_id}/unpack`);
      toast.success(`${row.part_number || "Line"} unpacked — freed back to Finished Goods stock`);
      setOpen(false);
      await onUnpacked();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Unpack failed");
    } finally {
      setUnpacking(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex" data-testid={`oss-packed-status-trigger-${row.id}`}>
          {badge}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-44 p-1" align="start" data-testid={`oss-packed-status-popover-${row.id}`}>
        <div className="px-2 py-1 text-[11px] text-slate-400">{row.packed_status}</div>
        <button
          type="button"
          onClick={unpack}
          disabled={unpacking}
          title="Reverts this Packing Slip's packed quantity to 0, freeing it back into available Finished Goods stock."
          className="w-full flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-amber-700 hover:bg-amber-50 disabled:opacity-50"
          data-testid={`oss-unpack-btn-${row.id}`}
        >
          {unpacking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageMinus className="w-3.5 h-3.5" />}
          Unpacked
        </button>
      </PopoverContent>
    </Popover>
  );
}

// Statuses the backend's own DELETE /sales-orders/{id} accepts (see
// server.py's delete_sales_order) — mirrored here only to decide whether
// to show the button at all; the backend remains the sole enforcer of who
// may delete what (e.g. marketing users can only delete their own).
const DELETABLE_SO_STATUSES = new Set(["draft", "order_raised", "cancelled"]);

// Deletes the whole Transfer Order this row belongs to (every line, not
// just this one) via the existing, already-audited Transfer Order delete
// route — this sheet has no delete route of its own, same pattern as the
// Unpack action above. `onDeleted` gets the so_id so the parent can drop
// every row that shared it, with no refetch needed.
function DeleteOrderCell({ row, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  if (!DELETABLE_SO_STATUSES.has(row.so_status)) {
    return <span className="text-slate-300">—</span>;
  }

  const remove = async () => {
    if (!window.confirm(`Delete Transfer Order ${row.to_no}? This removes all of its lines from this sheet.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/sales-orders/${row.so_id}`);
      toast.success(`${row.to_no} deleted`);
      onDeleted(row.so_id);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={remove}
      disabled={deleting}
      title={`Delete Transfer Order ${row.to_no}`}
      className="inline-flex items-center justify-center rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
      data-testid={`oss-delete-btn-${row.id}`}
    >
      {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
    </button>
  );
}

const PAGE_SIZES = [25, 50, 100, 250];

export default function OrderStatusSheet() {
  const { user } = useAuth();
  // Matches POST /packing-slips/{id}/unpack's own role check.
  const canUnpack = roleAllowed(user?.role, ["admin", "planner", "operator", "production_manager", "stores"]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [monthFilter, setMonthFilter] = useState("all");
  const [columnFilters, setColumnFilters] = useState({}); // field -> Set|null, applied on "OK" in the column's filter popover
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const loadRows = () =>
    api.get("/order-status-sheet/rows")
      .then((res) => setRows(res.data || []))
      .catch((err) => setError(formatApiError(err?.response?.data?.detail)));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/order-status-sheet/rows")
      .then((res) => { if (!cancelled) setRows(res.data || []); })
      .catch((err) => { if (!cancelled) setError(formatApiError(err?.response?.data?.detail)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Swaps in a freshly-saved row (from the Delivery Date PATCH response) in
  // place, so the edited cell and every column derived alongside it (e.g.
  // Final Delivery) update immediately — no refetch needed.
  const updateRow = (updated) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  // Drops every row belonging to the just-deleted Transfer Order (a TO with
  // several lines shows as several rows here, all deleted together).
  const removeOrderRows = (soId) => {
    setRows((prev) => prev.filter((r) => r.so_id !== soId));
  };

  // Distinct display-value options per column, computed once per data load
  // (independent of the currently-applied filters, so unchecking a value
  // in one column never makes another column's own option list shrink).
  // "Against Order Material Packed Status" is the one exception: its four
  // values are fixed (not derived from what the loaded rows happen to
  // contain), so "Unpacked" always has a checkbox right under "Partially
  // Packed" in that column's filter, even before any row has ever been
  // unpacked.
  const optionsByField = useMemo(() => {
    const map = {};
    for (const col of COLUMNS) {
      if (col.field === "packed_status") {
        map[col.field] = PACKED_STATUS_OPTIONS;
        continue;
      }
      const set = new Set();
      for (const r of rows) set.add(displayValue(r, col));
      map[col.field] = Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }
    return map;
  }, [rows]);

  const monthOptions = useMemo(() => {
    const present = new Set(rows.map((r) => r.month).filter(Boolean));
    return MONTH_ORDER.filter((m) => present.has(m));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (monthFilter !== "all" && r.month !== monthFilter) return false;
      for (const col of COLUMNS) {
        const sel = columnFilters[col.field];
        if (sel == null) continue; // no filter applied on this column
        if (!sel.has(displayValue(r, col))) return false;
      }
      return true;
    });
  }, [rows, monthFilter, columnFilters]);

  useEffect(() => { setPage(1); }, [monthFilter, columnFilters, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const activeFilterCount = Object.values(columnFilters).filter((s) => s != null).length + (monthFilter !== "all" ? 1 : 0);

  const resetFilters = () => { setColumnFilters({}); setMonthFilter("all"); };

  // Sums for the pinned totals row, recomputed from filteredRows so they
  // always reflect whatever filters (month + any column) are currently
  // applied — never the full, unfiltered dataset.
  // Money columns are summed per currency ({ INR, USD }) — rows priced in
  // different currencies are never added together.
  const totals = useMemo(() => {
    const sums = {};
    for (const field of TOTAL_FIELDS) {
      const col = COLUMNS.find((c) => c.field === field);
      sums[field] = col?.kind === "currency"
        ? totalsByCurrency(filteredRows, (r) => r[field])
        : filteredRows.reduce((acc, r) => acc + Number(r[field] || 0), 0);
    }
    return sums;
  }, [filteredRows]);
  const fmtTotal = (col) => (col.kind === "currency"
    ? formatCurrencyTotals(totals[col.field], { decimals: 2 })
    : fmtNumber(totals[col.field]));

  // Same "Total" label + 5 sums, as raw numbers for the Excel row.
  const totalsExportRow = COLUMNS.map((c, idx) => {
    if (idx === 0) return "Total";
    if (!TOTAL_FIELDS.has(c.field)) return "";
    if (c.kind !== "currency") return Number(totals[c.field] || 0);
    const byCur = totals[c.field] || {};
    const codes = Object.keys(byCur);
    // A single currency stays a plain number; mixed currencies are spelled out.
    return codes.length <= 1 ? Number(byCur[codes[0]] || 0) : fmtTotal(c);
  });

  const exportToExcel = () => {
    if (filteredRows.length === 0) {
      toast.error("No rows to export — adjust or clear the filters first.");
      return;
    }
    const aoa = [
      COLUMNS.map((c) => c.header),
      ...filteredRows.map((r) => COLUMNS.map((c) => {
        const v = c.field === "currency" ? docCurrency(r) : r[c.field];
        if (c.kind === "number" || c.kind === "currency" || c.kind === "percent") return Number(v || 0);
        return v == null ? "" : String(v);
      })),
      totalsExportRow,
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = COLUMNS.map(() => ({ wch: 16 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Order Status Sheet");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `order_status_sheet_${stamp}.xlsx`);
    toast.success(`Exported ${filteredRows.length} row${filteredRows.length === 1 ? "" : "s"} to Excel`);
  };

  return (
    <div className="max-w-full" data-testid="order-status-sheet-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <Table2 className="w-5 h-5 text-blue-600" /> Order Status Sheet
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Live, unified tracking sheet across Transfer Orders, Sales Order, Delivery Challans,
            Production Flow &amp; Invoices — read-only, except Delivery Date: click a cell to edit it,
            and the linked Sales Order's Delivery Date updates to match.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={monthFilter} onValueChange={setMonthFilter}>
            <SelectTrigger className="w-40 h-9" data-testid="oss-month-filter">
              <SelectValue placeholder="All months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {monthOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          {activeFilterCount > 0 && (
            <Button variant="outline" size="sm" onClick={resetFilters} data-testid="oss-reset-filters">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Clear filters ({activeFilterCount})
            </Button>
          )}
          <Button size="sm" onClick={exportToExcel} data-testid="oss-export-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export to Excel
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading order status sheet…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-24 text-slate-400 text-sm">No Transfer Orders found yet.</div>
      ) : (
        <>
          <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto overflow-y-auto max-h-[70vh] scroll-thin" data-testid="oss-table-wrap">
            <table className="min-w-max text-xs" data-testid="oss-table">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.field}
                      className="px-3 py-2 text-left font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap border-b border-slate-200"
                      data-testid={`oss-col-${col.field}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{col.header}</span>
                        <ColumnFilter
                          col={col}
                          options={optionsByField[col.field] || []}
                          selected={columnFilters[col.field]}
                          onChange={(sel) => setColumnFilters((prev) => ({ ...prev, [col.field]: sel }))}
                        />
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase tracking-wide whitespace-nowrap border-b border-slate-200" data-testid="oss-col-actions">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`oss-row-${row.id}`}>
                    {COLUMNS.map((col) => (
                      <td key={col.field} className="px-3 py-1.5 whitespace-nowrap text-slate-700">
                        {col.editable ? (
                          <DeliveryDateCell row={row} onSaved={updateRow} />
                        ) : col.field === "packed_status" ? (
                          <PackedStatusCell row={row} canUnpack={canUnpack} onUnpacked={loadRows} />
                        ) : col.kind === "badge" && row[col.field] ? (
                          <Badge className={`${BADGE_TONE[row[col.field]] || "bg-slate-100 text-slate-700"} font-normal border-0`}>
                            {row[col.field]}
                          </Badge>
                        ) : (
                          displayValue(row, col) || <span className="text-slate-300">—</span>
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-1.5 whitespace-nowrap text-slate-700">
                      <DeleteOrderCell row={row} onDeleted={removeOrderRows} />
                    </td>
                  </tr>
                ))}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="text-center py-12 text-slate-400">
                      No rows match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-slate-50">
                <tr className="border-t-2 border-slate-300" data-testid="oss-totals-row">
                  {COLUMNS.map((col, idx) => (
                    <td
                      key={col.field}
                      className="px-3 py-2 whitespace-nowrap font-bold text-slate-900"
                      data-testid={`oss-total-${col.field}`}
                    >
                      {idx === 0
                        ? "Total"
                        : TOTAL_FIELDS.has(col.field)
                        ? fmtTotal(col)
                        : ""}
                    </td>
                  ))}
                  <td className="px-3 py-2 whitespace-nowrap" data-testid="oss-total-actions" />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-sm text-slate-600">
            <div data-testid="oss-row-count">
              Showing {pageRows.length === 0 ? 0 : (page - 1) * pageSize + 1}
              –{Math.min(page * pageSize, filteredRows.length)} of {filteredRows.length} row{filteredRows.length === 1 ? "" : "s"}
              {filteredRows.length !== rows.length ? ` (filtered from ${rows.length})` : ""}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500">Rows per page</span>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="w-20 h-8 text-xs" data-testid="oss-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage(1)} data-testid="oss-page-first">«</Button>
                <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="oss-page-prev">‹</Button>
                <span className="text-xs px-2">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="oss-page-next">›</Button>
                <Button variant="outline" size="sm" className="h-8 px-2.5" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="oss-page-last">»</Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
